// Shared TinyMCE configuration factory
export interface TinyMCEConfigOptions {
  direction?: 'ltr' | 'rtl';
  height?: number;
  menubar?: boolean;
  language?: string;
  mode?: 'full' | 'inline' | 'compact';
  uploadHandler?: (
    blobInfo: any,
    success: (url: string) => void,
    failure: (msg: string) => void,
    progress?: (percent: number) => void
  ) => void;
}

// TinyMCE upload API response interface
export interface UploadResponse {
  location?: string;
  url?: string;
  error?: string;
  message?: string;
}

// Base plugins used in most configurations (bez problematycznego help)
const BASE_PLUGINS = [
  'advlist',
  'autolink',
  'lists',
  'link',
  'image',
  'code',
  'fullscreen',
  'wordcount',
];

// Additional plugins for full editor (bez problematycznego help)
const FULL_PLUGINS = [
  ...BASE_PLUGINS,
  'charmap',
  'preview',
  'anchor',
  'searchreplace',
  'visualblocks',
  'insertdatetime',
  'media',
  'table',
];

// Toolbar configurations (bez help button)
const TOOLBARS = {
  full:
    'undo redo | blocks | bold italic underline strikethrough forecolor backcolor | ' +
    'alignleft aligncenter alignright alignjustify | bullist numlist outdent indent | ' +
    'link image media table | removeformat code fullscreen',

  compact:
    'undo redo | bold italic underline forecolor backcolor | ' +
    'alignleft aligncenter alignright alignjustify | bullist numlist outdent indent | removeformat code fullscreen',

  inline:
    'undo redo | bold italic underline forecolor backcolor | ' +
    'alignleft aligncenter alignright alignjustify | bullist numlist | removeformat',
};

export const createTinyMCEConfig = (options: TinyMCEConfigOptions = {}) => {
  const { direction = 'ltr', height = 400, menubar = true, mode = 'full', uploadHandler } = options;

  const publicUrl = process.env.PUBLIC_URL || '';
  const textAlign = direction === 'rtl' ? 'right' : 'left';
  const plugins = mode === 'full' ? FULL_PLUGINS : BASE_PLUGINS;
  const toolbar = TOOLBARS[mode] || TOOLBARS.full;

  const config: any = {
    height,
    menubar,
    language: 'pl', // Forcowanie języka polskiego
    plugins,
    toolbar,
    block_formats: 'Nagłówek 1=h1;Nagłówek 2=h2;Nagłówek 3=h3;Paragraf=p',
    content_style: `body, p, div, span, textarea { 
      font-family:Helvetica,Arial,sans-serif; 
      font-size:14px; 
      direction: ${direction} !important; 
      unicode-bidi: plaintext !important;
      text-align: ${textAlign} !important; 
    }`,
    base_url: `${publicUrl}/tinymce`,
    suffix: '.min',
    directionality: direction,
    // Dodatkowe ustawienia dla stabilności
    convert_urls: false,
    relative_urls: false,
    remove_script_host: false,
    // Wyłączenie problematycznych funkcji językowych
    language_url: false,
    // Timeout dla ładowania zasobów
    script_loader_timeout: 10000,
  };

  // Add upload handler only if provided
  if (uploadHandler) {
    config.images_upload_handler = uploadHandler;
    config.automatic_uploads = true;
    config.file_picker_types = 'image';
    config.images_upload_base_path = '';
    config.images_reuse_filename = true;
    config.paste_data_images = true;
    config.images_upload_credentials = false;
    config.convert_urls = false;
    // Additional settings to help with upload issues
    config.relative_urls = false;
    config.remove_script_host = false;
    // Add debug logging
    config.setup = (editor: any) => {
      editor.on('init', () => {
        console.log('TinyMCE initialized successfully');
      });
    };
  }

  return config;
};

// Pre-configured instances
export const TinyMCEConfigs = {
  // Full editor for forms
  form: (direction?: 'ltr' | 'rtl', uploadHandler?: any) =>
    createTinyMCEConfig({
      direction,
      height: 400,
      menubar: true,
      mode: 'full',
      uploadHandler,
    }),

  // Compact editor for cell editing
  cell: (direction?: 'ltr' | 'rtl') =>
    createTinyMCEConfig({
      direction,
      height: 160,
      menubar: false,
      mode: 'inline',
    }),

  // Inline editor for quick edits
  inline: (direction?: 'ltr' | 'rtl') =>
    createTinyMCEConfig({
      direction,
      height: 200,
      menubar: false,
      mode: 'compact',
    }),
};
